import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageSeo } from "@/components/PageSeo";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Upload, Sparkles, Download, Clock, DollarSign, Shield } from "lucide-react";

const WhatIsEstyling = () => {
  const { t } = useTranslation();
  const { buildPath } = useLocalizedPath();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageSeo />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-primary/5 to-background py-16 sm:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                {t('seoPages.whatIsEstyling.hero.title')}
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                {t('seoPages.whatIsEstyling.hero.subtitle')}
              </p>
              <Button asChild size="lg" className="text-lg px-8">
                <Link to={buildPath('/auth')}>{t('seoPages.whatIsEstyling.cta')}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* What is E-styling */}
        <section className="py-16 sm:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                {t('seoPages.whatIsEstyling.definition.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {t('seoPages.whatIsEstyling.definition.text')}
              </p>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                {t('seoPages.whatIsEstyling.howItWorks.title')}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: Upload, key: 'step1' },
                  { icon: Sparkles, key: 'step2' },
                  { icon: Clock, key: 'step3' },
                  { icon: Download, key: 'step4' },
                ].map(({ icon: Icon, key }, index) => (
                  <Card key={key} className="text-center">
                    <CardContent className="pt-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-2xl font-bold text-primary mb-2">{index + 1}</div>
                      <h3 className="font-semibold mb-2 text-foreground">
                        {t(`seoPages.whatIsEstyling.howItWorks.${key}.title`)}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t(`seoPages.whatIsEstyling.howItWorks.${key}.text`)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 sm:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                {t('seoPages.whatIsEstyling.benefits.title')}
              </h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {['fast', 'affordable', 'realistic', 'easy', 'flexible', 'professional'].map((benefit) => (
                  <div key={benefit} className="flex items-start gap-4">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold mb-1 text-foreground">
                        {t(`seoPages.whatIsEstyling.benefits.items.${benefit}.title`)}
                      </h3>
                      <p className="text-muted-foreground">
                        {t(`seoPages.whatIsEstyling.benefits.items.${benefit}.text`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Who is it for */}
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-foreground">
                {t('seoPages.whatIsEstyling.forWhom.title')}
              </h2>
              <p className="text-lg text-muted-foreground text-center mb-12">
                {t('seoPages.whatIsEstyling.forWhom.intro')}
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {['realtors', 'sellers', 'landlords', 'developers'].map((group) => (
                  <Card key={group}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-2 text-foreground">
                        {t(`seoPages.whatIsEstyling.forWhom.groups.${group}.title`)}
                      </h3>
                      <p className="text-muted-foreground">
                        {t(`seoPages.whatIsEstyling.forWhom.groups.${group}.text`)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('seoPages.whatIsEstyling.finalCta.title')}
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              {t('seoPages.whatIsEstyling.finalCta.text')}
            </p>
            <Button asChild size="lg" variant="secondary" className="text-lg px-8">
              <Link to={buildPath('/auth')}>{t('seoPages.whatIsEstyling.cta')}</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WhatIsEstyling;
